import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Typography, Box, Container, Modal, MenuItem, Button } from "@mui/material";
import ModeEditOutlinedIcon from "@mui/icons-material/ModeEditOutlined";
import useMediaQuery from "@mui/material/useMediaQuery";
import { useTheme } from "@mui/material/styles";
import ConfirmStopModal from "../../shared/ConfirmStopModal";
import RequestStatus from "../../shared/request/RequestStatus";
import ActionButton from "../../shared/ActionButton";
import NoticeBanner from "../../shared/NoticeBanner";
import SearchContainer from "../shared/search/SearchContainer";
import SearchTabs from "../shared/search/filter/SearchTabs";
import MenuModal from "../../shared/MenuModal";
import Positions from "../shared/search/filter/Positions";
import Price from "../shared/search/filter/Price";
import Prefectures from "../shared/search/filter/Prefecture";
import ModalSp from "../../shared/ModalForSp";
import FilterTag from "../shared/search/FilterTag";
import useActiveFilterTags from "../../shared/hooks/useActiveFilterTags";
import { useFilterModal } from "../../shared/hooks/useFilterModal";
import { FilterModalHeader, FilterModalFooter } from "../../shared/FilterModalParts";
import useRemoveFilterTagAndSearch from "../../shared/hooks/useRemoveFilterTagAndSearch";
import { useIsDesktop } from "../../shared/hooks/useIsDesktop";
import { useSearchableIndex } from "../../../controllers/useSearchableIndex";
import { buildPrefByArea } from "../../utils/prefectureArea";
import { getMasterData } from "../../utils/masterDataClient";
import usePriceFilterInSearchFilters, { createInitialPriceFilter } from "../../shared/hooks/usePriceFilterInSearchFilters";
import {
  JOB_COLOR,
  JOB_COLOR_LIGHT,
  JOB_COLOR_DARK,
  GRAY_TEXT_COLOR,
  TEXT_COLOR_PRIMARY,
  BORDER_COLOR_LIGHT,
} from "../../../constants/colors";

const shouldShowJobSearch = (pathname) => {
  // 求人一覧画面でのみ表示
  return pathname === "/jobs" || pathname === "/jobs/";
};

const createInitialJobSearchFilters = () => ({
  keyword: [],
  keywordInputText: "",
  roleIds: [],
  prefectureIds: [],
  priceFilter: createInitialPriceFilter()
});

const initialPriceFilter = createInitialPriceFilter();

export default function JobLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const showSearchContainer = shouldShowJobSearch(location.pathname);

  // レスポンシブ
  const theme = useTheme();
  const isDesktop = useIsDesktop();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  // 表示する求人一覧
  const [jobs, setJobs] = useState([]);

  // 一覧取得中フラグ
  const [loading, setLoading] = useState(true);

  // 希望条件が入力済み
  const [jobExists, setJobExists] = useState(null);

  // 公開状態
  const [isActive, setIsActive] = useState(false);

  // モーダル
  const [modalOpen, setModalOpen] = useState(false);

  // モーダルで確定待ちの状態（true: ON / false: OFF）
  const [pendingStatus, setPendingStatus] = useState(null);

  // スマホ用絞り込みメニューモーダル
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const menuOpen = Boolean(menuAnchorEl);

  // 絞り込みモーダル
  const [filterModalOpen, setFilterModalOpen] = useState(false);

  // 選択中の絞り込み条件
  const [activeFilter, setActiveFilter] = useState(null);

  // ポジション
  const [roles, setRoles] = useState([]);

  // 勤務地
  const [prefectures, setPrefectures] = useState([]);

  // /jobs.jsonを取得してjobsを更新
  const fetchJobs = useCallback(async () => {
    setLoading(true);
    try {
      const res = await fetch("/jobs.json", {
        credentials: "same-origin",
        cache: "no-store",
      });
  
      if (!res.ok) {
        setJobs([]);
        return;
      }
  
      const data = await res.json();
      setJobs(data);
    } catch {
      setJobs([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const {
    searchFilters,
    setSearchFilters,
    hasSearchFilters,
    hasExecutedSearch,
    markSearchAsNotExecuted,
    executeSearch,
    searchResults,
    isSearching,
  } = useSearchableIndex({
    searchEndpoint: "/api/jobs/search",
    fetchIndex: fetchJobs,
    createInitialSearchFilters: createInitialJobSearchFilters,
    getResponseItems: (json) => json.jobs,
  });

  const { setPriceFilterInSearchFilters } = usePriceFilterInSearchFilters({
    setSearchFilters,
    fallbackPriceFilter: initialPriceFilter,
  });

  const { handleRemoveFilterTagAndSearch } = useRemoveFilterTagAndSearch({
    setSearchFilters,
    executeSearch,
    prefectures,
  });

  // ポジションと都道府県を一覧で取得
  useEffect(() => {
    getMasterData()
      .then((masterData) => {
        setRoles(masterData.roles);
        setPrefectures(masterData.prefectures);
      })
      .catch((error) => {
        console.error("/api/master fetch failed", error);
        toast.error(
          "情報の読み込みに失敗しました。再読み込みしてください。"
        );
        setRoles([]);
        setPrefectures([]);
      });
  }, []);

  // 再レンダリングのたびに分類処理を走らせないため useMemo でキャッシュ
  const prefByArea = useMemo(() => buildPrefByArea(prefectures), [prefectures]);

  // 選択されたポジションを表示(未選択の場合は「ポジション」を表示)
  const selectedRoleLabel = useMemo(() => {
    if (!searchFilters.roleIds?.length) return "";

    const selectedRoleIdSet = new Set(searchFilters.roleIds.map(String));

    const selectedRoleNames = roles
      .filter((role) => selectedRoleIdSet.has(String(role.id)))
      .map((role) => role.name);

    return selectedRoleNames.join("、");
  }, [searchFilters.roleIds, roles]);

  // 選択された金額を表示(金額: ◯万円／未選択の場合は「年収」を表示)
  const selectedIncomeLabel = useMemo(() => {
    const priceValue = String(searchFilters.priceFilter?.value ?? "").trim();
    if (priceValue === "") return "";
    return `${priceValue}万円`;
  }, [searchFilters.priceFilter]);

  // 選択された勤務地を表示(未選択の場合は「勤務地」を表示)
  const selectedPrefectureLabel = useMemo(() => {
    if (!searchFilters.prefectureIds?.length) return "";
  
    const selectedPrefectureIdSet = new Set(searchFilters.prefectureIds.map(String));
  
    const selectedPrefectureNames = prefectures
      .filter((prefecture) => selectedPrefectureIdSet.has(String(prefecture.id)))
      .map((prefecture) => prefecture.name);
  
    return selectedPrefectureNames.join("、");
  }, [searchFilters.prefectureIds, prefectures]);

  // PC: 選択・指定した条件をタグにする
  const { activeFilterTags } = useActiveFilterTags({
    searchFilters,
    roles,
    prefectures,
  });

  // スマホならメニューモーダル、PCなら絞り込みモーダルを表示
  const [filterDialogOpen, setFilterDialogOpen] = useState(false);

  const {
    handleOpen,
    handleClose,
    modalStyle,
    deleteFilter,
    openFilterModal,
    modalTitle,
    modalHeaderSx,
    modalFooterOkButtonSx,
    handleSubmit,
  } = useFilterModal({
    isMobile,
    setMenuAnchorEl,
    setFilterDialogOpen,
    setFilterModalOpen,
    filterTitleMap: { price: "年収" },
    setActiveFilter,
    activeFilter,
    executeSearch,
    searchFilters,
    setSearchFilters,
    selectedColor: TEXT_COLOR_PRIMARY,
    projectColor: JOB_COLOR,
    projectColorDark: JOB_COLOR_DARK,
  });

  const modalHeader = (
    <FilterModalHeader title={modalTitle} headerSx={modalHeaderSx} />
  );
  
  const modalFooter = (
    <FilterModalFooter
      onCancel={handleClose}
      onSubmit={handleSubmit}
      leftLabel="キャンセル"
      rightLabel="OK"
      ActionButton={ActionButton}
      cancelButtonSx={{ bgcolor: "#D9D9D9" }}
      submitButtonSx={modalFooterOkButtonSx}
    />
  );

  const shouldShowSearchResults = hasSearchFilters && (hasExecutedSearch || isSearching);
  // 表示する一覧は「検索中は searchResults、それ以外は projects」
  const visibleJobs = shouldShowSearchResults ? searchResults : jobs;

  // loading も検索中は isSearching を使う
  const visibleLoading = shouldShowSearchResults ? isSearching : loading;

  // 初回ロード
  useEffect(() => {
    fetch("/user_job_request/edit.json")
      .then((res) => res.json())
      .then((data) => {
        setJobExists(data.job_request_exists);
        setIsActive(data.recruiting_flag);
      })
      .catch((err) => {
        console.error("Error loading request:", err);
        toast.error("読み込みに失敗しました。", { toastId: "fetch-error" });
      });
  }, []);

  // 公開フラグ更新
  const updateRecruitingFlag = (newStatus) => {
    fetch("/user_job_request/toggle_recruiting", {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-CSRF-Token": document.querySelector("meta[name='csrf-token']")
          ?.content,
      },
      body: JSON.stringify({
        user_job_request: {
          recruiting_flag: newStatus,
        },
      }),
    }).then((res) => {
      if (res.ok) {
        // 自分の state 更新
        setIsActive(newStatus);

        // JobDetailPage に通知
        window.dispatchEvent(
          new CustomEvent("job:status-updated", {
            detail: { recruiting_flag: newStatus },
          }),
        );

        toast.success(
          newStatus ? "公開に変更しました。" : "非公開に変更しました。",
        );
      } else {
        toast.error("失敗しました。");
      }
    });
  };

  // ステータス切り替え（クリック時）
  const handleChangeStatus = (next) => {
    // 次にしたい状態を保存
    setPendingStatus(next);

    // ON / OFF どちらでも必ずモーダル表示
    setModalOpen(true);
  };

  // モーダル：OFF を確定
  const handleConfirmStop = () => {
    if (pendingStatus === null) return;

    // 確定した状態で更新
    updateRecruitingFlag(pendingStatus);

    // モーダルを閉じてリセット
    setModalOpen(false);
    setPendingStatus(null);
  };

  // モーダル：キャンセル
  const handleCancelStop = () => {
    setModalOpen(false);
    setPendingStatus(null);
  };

  // 既読
  const handleMarkedRead = useCallback((jobId) => {
    setJobs((prev) =>
      prev.map((j) => (j.id === jobId ? { ...j, read_flag: true } : j))
    );
  }, []);

  // 遷移先
  const handleClick = () => {
    navigate(`/user_job_request/edit?redirect=${window.location.pathname}`);
  };

  // 検索後検索結果先頭へスクロール
  const searchResultsTopRef = useRef(null);
  const scrollToSearchResultsTop = useCallback(() => {
    const searchResultsTopElement = searchResultsTopRef.current;
    if (!searchResultsTopElement) return;

    searchResultsTopElement.scrollIntoView({ block: "start", behavior: "auto" });
    searchResultsTopElement.focus();
  }, []);

  return (
    <Container
      maxWidth="md"
      disableGutters
      sx={{
        position: "relative",
        display: "flex",
        flexDirection: "column",
      }}
    >
      {/* ===== 固定エリア ===== */}
      <Box
        sx={{
          position: "sticky",
          top: isDesktop ? "63px" : "45px",
          zIndex: 20,
          backgroundColor: "#F4F7FA",
          boxShadow: 0.5,
          mx: -2,
          px: isDesktop ? 0 : 2,
          pt: isDesktop ? 1.5 : 1,
        }}
      >
        {/* --- 上段：タイトル / RequestStatus --- */}
        <Box sx={{ mb: 1 }}>
          <Box
            sx={{
              display: "flex",
              flexDirection: isDesktop ? "row" : "column",
              alignItems: isDesktop ? "center" : "stretch",
              gap: isDesktop ? 1.5 : 1,
            }}
          >
            {/* タイトル */}
            <Typography
              fontWeight="bold"
              sx={{
                fontSize: isDesktop ? "1.8rem" : "1.2rem",
                whiteSpace: "nowrap",
              }}
            >
              求人
            </Typography>

            {/* タイトル寄りの RequestStatus */}
            {jobExists && (
              <RequestStatus
                viewerType="user"
                type="job"
                isActive={isActive}
                onToggle={handleChangeStatus}
              />
            )}

            {/* NoticeBanner + ActionButton */}
            <Box
              sx={{
                ml: isDesktop ? "auto" : 0,
                display: "flex",
                flexDirection: isDesktop ? "row" : "column-reverse",
                gap: 1,
                width: isDesktop ? "auto" : "100%",
                alignItems: "center",
              }}
            >
              <NoticeBanner
                text={
                  jobExists
                    ? "希望条件に変更があれば、更新しておきましょう。"
                    : "希望条件を入力すると、詳細を全て閲覧できます。"
                }
              />

              <ActionButton
                onClick={handleClick}
                label={`希望条件を${jobExists ? "編集" : "入力"}`}
                icon={<ModeEditOutlinedIcon sx={{ color: JOB_COLOR }} />}
                showBadge={!jobExists}
                sx={{
                  bgcolor: JOB_COLOR,
                  width: isDesktop ? "auto" : "100%",
                  "&:hover": { bgcolor: JOB_COLOR_DARK },
                }}
                badgeSx={{
                  bgcolor: JOB_COLOR_LIGHT,
                }}
              />
            </Box>
          </Box>
        </Box>
        {/* 求人一覧のみ表示 */}
        {showSearchContainer && (
          <Box sx={{ mt: 1 }}>
            <SearchContainer
              type="job"
              value={searchFilters}
              onChange={(nextFilters) => {
                setSearchFilters(nextFilters);
                if (!isSearching) {
                  markSearchAsNotExecuted();
                }
              }}
              onSearch={(filters) => {
                executeSearch(filters);
                scrollToSearchResultsTop();
              }}
              onOpenFilter={handleOpen}
            />
            {isDesktop && activeFilterTags.length > 0 && (
              <Box>
                <FilterTag
                  type="job"
                  selectedKeywords={activeFilterTags}
                  onDeleteTag={(keywordIndex) => {
                    const tagToRemove = activeFilterTags[keywordIndex];
                    if (!tagToRemove) return;
                    handleRemoveFilterTagAndSearch(tagToRemove);
                  }}
                />
                <Button
                  variant="text"
                  onClick={() => {
                    const nextFilters = {
                      ...searchFilters,
                      roleIds: [],
                      prefectureIds: [],
                      priceFilter: createInitialPriceFilter(),
                    };

                    setSearchFilters(nextFilters);
                    executeSearch(nextFilters);
                  }}
                  sx={{ color: GRAY_TEXT_COLOR, mt: 1 }}
                >
                  フィルターをリセット
                </Button>
              </Box>
            )}
            {isMobile ? (
              <>
                <MenuModal
                  anchorEl={menuAnchorEl}
                  open={menuOpen}
                  onClose={() => setMenuAnchorEl(null)}
                  paperSx={{ width: "100%", borderRadius: 1 }}
                >
                  <>
                    <MenuItem
                      onClick={() => {
                        openFilterModal("position"); // ModalForSp を Positionで開く
                      }}
                      sx={{ borderBottom: `1px solid ${BORDER_COLOR_LIGHT}` }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                        <Typography sx={{ color: searchFilters.roleIds.length > 0 ? TEXT_COLOR_PRIMARY : GRAY_TEXT_COLOR, textOverflow: "ellipsis", overflow: "hidden", }}>
                          {searchFilters.roleIds.length > 0 ? selectedRoleLabel : "ポジション"}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        openFilterModal("price"); // ModalForSp を Priceで開く
                      }}
                      sx={{ borderBottom: `1px solid ${BORDER_COLOR_LIGHT}` }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                        <Typography sx={{ color: selectedIncomeLabel.length > 0 ? TEXT_COLOR_PRIMARY : GRAY_TEXT_COLOR, textOverflow: "ellipsis", overflow: "hidden", }}>
                          {selectedIncomeLabel !== "" ? selectedIncomeLabel : "年収"}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        openFilterModal("prefecture"); // ModalForSp を Prefectureで開く
                      }}
                      sx={{ borderBottom: `1px solid ${BORDER_COLOR_LIGHT}` }}
                    >
                      <Box sx={{ display: "flex", justifyContent: "space-between", width: "100%" }}>
                        <Typography sx={{ color: searchFilters.prefectureIds.length > 0 ? TEXT_COLOR_PRIMARY : GRAY_TEXT_COLOR, textOverflow: "ellipsis", overflow: "hidden", }}>
                          {searchFilters.prefectureIds.length > 0 ? selectedPrefectureLabel : "勤務地"}
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem
                      onClick={() => {
                        deleteFilter();
                        // NOTE: Mobile UX spec:
                        // Do NOT call setMenuAnchorEl(null) intentionally.
                        // Keep MenuModal open so users can see the currently applied conditions after closing FilterModal.
                        // Keep MenuModal open to immediately reflect the cleared conditions in this panel.
                      }}
                      sx={{ color: GRAY_TEXT_COLOR }}
                    >
                      フィルターをリセット
                    </MenuItem>
                  </>
                </MenuModal>
                <ModalSp
                  open={filterModalOpen}
                  onClose={handleClose}
                  header={({ modalTitleId }) => (
                    <FilterModalHeader
                      title={modalTitle}
                      headerSx={modalHeaderSx}
                      titleId={modalTitleId}
                    />
                  )}
                  footer={modalFooter}
                >
                  {activeFilter === "position" && (
                    <Positions
                      roles={roles}
                      selectedRoleIds={searchFilters.roleIds}
                      onChangeSelectedRoleIds={(nextRoleIds) => {
                        setSearchFilters((previousFilters) => ({
                          ...previousFilters,
                          roleIds: nextRoleIds,
                        }));
                      }}
                    />
                  )}

                  {activeFilter === "price" && (
                    <Price
                      priceFilter={searchFilters.priceFilter}
                      setPriceFilter={setPriceFilterInSearchFilters}
                      label="希望年収"
                    />
                  )}

                  {activeFilter === "prefecture" && (
                    <Prefectures
                      prefByArea={prefByArea}
                      selectedIds={searchFilters.prefectureIds}
                      setSelectedIds={(nextPrefectureIds) => {
                        setSearchFilters((previousFilters) => ({
                          ...previousFilters,
                          prefectureIds: nextPrefectureIds,
                        }));
                      }}
                      isOpen={filterModalOpen}
                    />
                  )}
                </ModalSp>
              </>
            ) : (
              <Modal
                open={filterDialogOpen}
                onClose={handleClose}
                aria-labelledby="job-filter-modal-title"
              >
                <Box sx={modalStyle}>
                <Typography
                    id="job-filter-modal-title"
                    variant="h6"
                    sx={{
                      position: "absolute",
                      width: "1px",
                      height: "1px",
                      padding: 0,
                      margin: "-1px",
                      overflow: "hidden",
                      clip: "rect(0, 0, 0, 0)",
                      whiteSpace: "nowrap",
                      border: 0,
                    }}
                  >
                    検索条件の絞り込み
                  </Typography>
                  <SearchTabs
                    roles={roles}
                    prefByArea={prefByArea}
                    selectedRoleIds={searchFilters.roleIds}
                    setSelectedRoleIds={(nextRoleIds) => {
                      setSearchFilters((previousFilters) => ({
                        ...previousFilters,
                        roleIds: nextRoleIds,
                      }));
                    }}
                    selectedPrefectureIds={searchFilters.prefectureIds}
                    setSelectedPrefectureIds={(nextPrefectureIds) => {
                      setSearchFilters((previousFilters) => ({
                        ...previousFilters,
                        prefectureIds: nextPrefectureIds,
                      }));
                    }}
                    priceFilter={searchFilters.priceFilter}
                    setPriceFilter={setPriceFilterInSearchFilters}
                    PriceComponent={Price}
                    type="job"
                    tabLabels={{ roles: "ポジション", price: "年収", prefecture: "勤務地" }}
                    onSearch={executeSearch}
                    onClose={handleClose}
                  />
                </Box>
              </Modal>
            )}
          </Box>
        )}
      </Box>

      {/* ===== スクロール領域 ===== */}
      <Box
        sx={{
          flex: 1,
        }}
      >
        <Box
          ref={searchResultsTopRef}
          tabIndex={-1}
          sx={{
            outline: "none",
            // sticky エリアの高さ分
            scrollMarginTop: { xs: "360px", sm: "220px" },
          }}
        >
          <Outlet
            context={{
              jobExists,
              isActive,
              jobs: visibleJobs,
              loading: visibleLoading,
              hasSearchFilters,
              hasExecutedSearch,
              isSearching,
              fetchJobs,
              setJobs,
              handleMarkedRead,
              fetchJobsBySearch: executeSearch,
            }}
          />
        </Box>
      </Box>

      {/* --- モーダル --- */}
      <ConfirmStopModal
        open={modalOpen}
        onClose={handleCancelStop}
        onConfirm={handleConfirmStop}
        title={isActive ? "スキルログ運営局に非公開" : "スキルログ運営局に公開"}
        message={
          isActive
            ? "非公開にすると運営局とのコンタクトができなくなります。"
            : "公開すると運営局からの連絡やプロジェクト紹介を受け取れます。"
        }
        confirmLabel={isActive ? "非公開にする" : "公開する"}
      />
    </Container>
  );
}
